import dayjs, { Dayjs } from 'dayjs';
import { LocationInventorySync } from '../../app/models/LocationInventorySync';
import { SyncLog } from '../../app/models/SyncLog';

export type SyncHealth = 'healthy' | 'running' | 'delayed' | 'failed';

export interface SyncHealthJob {
  id: string;
  categoryKey: string;
  category: string;
  name: string;
  frequency: string;
  frequencyMinutes: number | null;
  recordsProcessed: number;
  health: SyncHealth;
  lastCompletedAt: string | null;
  startedAt: string | null;
  duration: string;
  nextRunAt: string | null;
  errorMessage: string | null;
}

export interface SyncHealthGroup {
  key: string;
  name: string;
  jobs: SyncHealthJob[];
}

const frequencyLabels: Record<string, string> = {
  Customers: '15 min',
  Products: '15 min',
  ProductVariants: '15 min',
  Orders: '30 min',
  OrderFulfillments: '15 min',
  fulfillmentTrackingInfoUpdate: '15 min',
  CompanyLocationCredit: '15 min',
  CompanyLocationAccessToken: 'Weekly',
  Inventory: '1 hour',
  ProductSalesCat: '4 hours',
  Locations: '4 hours',
  CompanyLocations: '4 hours',
  CustomerLevel: '4 hours',
  ShowroomsInventory: '4 hours',
  SalesReps: '24 hours',
  CompanyLocationBillingAddress: '4 hours',
  CompanyLocationShippingAddress: '4 hours',
  CompanyLocationCustomerLevel: '4 hours',
  CompanyLocationSalesRep: '4 hours',
  CompanyLocationPaymentTerms: '4 hours',
  PriceList: '24 hours',
  PriceListPrices: '24 hours',
  ProductVariantPrice: '24 hours',
  PaymentReconciliation: 'After order sync',
};

const frequencyMinutes: Record<string, number> = {
  '15 min': 15,
  '30 min': 30,
  '1 hour': 60,
  '4 hours': 4 * 60,
  '24 hours': 24 * 60,
  Weekly: 7 * 24 * 60,
  'After order sync': 30,
};

// Preserve the existing dashboard's tolerance while covering the previously
// unhandled 30-minute schedule as well.
const delayedAfterMinutes: Record<string, number> = {
  '15 min': 45,
  '30 min': 90,
  '1 hour': 2 * 60,
  '4 hours': 5 * 60,
  '24 hours': 25 * 60,
  Weekly: 8 * 24 * 60,
  'After order sync': 90,
};

const propertyAliases: Record<string, string> = {
  'companyLocationUpdate/updateType=Billing': 'CompanyLocationBillingAddress',
  'companyLocationUpdate/updateType=Shipping': 'CompanyLocationShippingAddress',
  'metafieldsSet/metafieldsSet/companyLocations/updateType=CustomerLevel': 'CompanyLocationCustomerLevel',
  'companyLocationUpdate/updateType=SalesRep': 'CompanyLocationSalesRep',
  'companyLocationUpdate/updateType=Credit': 'CompanyLocationCredit',
  'companyLocationUpdate/updateType=PaymentTerms': 'CompanyLocationPaymentTerms',
  'companyLocationUpdate/updateType=Token': 'CompanyLocationAccessToken',
  companyLocations: 'CompanyLocation',
  fulfillmentCreateV2: 'OrderFulfillments',
  'metafieldsSet/namespace=custom?key=showroom_inventory': 'ShowroomsInventory',
  'metafieldsSet/namespace=custom?key=sales_cat': 'ProductSalesCat',
  'metaobjectUpsert/type=sales_rep': 'SalesReps',
  ProductVariantPrice: 'ProductVariantPrice',
  productVariantUpdate: 'ProductVariantPrice',
  productVariantsBulkUpdate: 'ProductVariantPrice',
  orders: 'Orders',
  'paymentReconciliation/evaluation': 'PaymentReconciliation',
};

const propertyDisplayNames: Record<string, string> = {
  'companyLocationUpdate/updateType=Billing': 'Billing address',
  'companyLocationUpdate/updateType=Shipping': 'Shipping address',
  'metafieldsSet/metafieldsSet/companyLocations/updateType=CustomerLevel': 'Customer level',
  'companyLocationUpdate/updateType=PaymentTerms': 'Payment terms',
  'companyLocationUpdate/updateType=SalesRep': 'Sales representative',
  'companyLocationUpdate/updateType=Credit': 'Credit',
  'companyLocationUpdate/updateType=Token': 'Access token',
  companyLocations: 'Company locations',
  fulfillmentCreateV2: 'Fulfillments',
  fulfillmentTrackingInfoUpdate: 'Tracking updates',
  'metafieldsSet/namespace=custom?key=showroom_inventory': 'Product variant showroom inventory',
  'metafieldsSet/namespace=custom?key=sales_cat': 'Product sales category',
  'metaobjectUpsert/type=sales_rep': 'Sales representatives',
  productVariantUpdate: 'Prices',
  productVariantsBulkUpdate: 'Variant prices',
  orders: 'Orders',
  'paymentReconciliation/evaluation': 'Evaluation',
};

const categoryDisplayNames: Record<string, string> = {
  Order: 'Orders',
  PaymentReconciliation: 'Payment reconciliation',
  CompanyLocation: 'Company locations',
  FulfillmentOrder: 'Fulfillment orders',
  Metafield: 'Metafields',
  MetaObject: 'Metaobjects',
  ProductVariantPrice: 'Product variants',
  Inventory: 'Inventory',
  PriceList: 'Price lists',
};

const categoryOrder = [
  'Order',
  'PaymentReconciliation',
  'CompanyLocation',
  'FulfillmentOrder',
  'Metafield',
  'MetaObject',
  'ProductVariantPrice',
  'Inventory',
  'PriceList',
];

const normalizedCategory = (syncObject: string) => {
  if (syncObject === 'Product') return 'Metafield';
  if (syncObject === 'PriceListPrices') return 'PriceList';
  return syncObject;
};

const getLastCompletedAt = (log: SyncLog) => log.completedAt || log.lastCompletedAt;

const getLatestError = (log: SyncLog) => {
  if (!log.errorLogs?.length) return null;

  return [...log.errorLogs]
    .sort((a, b) => dayjs(b.logTime).valueOf() - dayjs(a.logTime).valueOf())[0]
    ?.error || null;
};

const formatDuration = (startedAt: string | null, completedAt: string | null, now: Dayjs) => {
  if (!startedAt) return '—';

  const start = dayjs(startedAt);
  const end = completedAt ? dayjs(completedAt) : now;
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return '—';

  const totalSeconds = Math.max(0, end.diff(start, 'second'));
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m ${totalSeconds % 60}s`;

  const hours = Math.floor(totalMinutes / 60);
  return `${hours}h ${totalMinutes % 60}m`;
};

const calculateHealth = (
  log: SyncLog,
  frequency: string,
  completedAt: string | null,
  now: Dayjs,
): SyncHealth => {
  if (log.errorLogs?.length) return 'failed';
  if (log.inProgress) return 'running';
  if (!completedAt) return 'delayed';

  const completed = dayjs(completedAt);
  const threshold = delayedAfterMinutes[frequency];
  if (!completed.isValid() || !threshold) return 'delayed';

  return now.diff(completed, 'minute', true) > threshold ? 'delayed' : 'healthy';
};

export const buildSyncHealthJobs = (
  syncLogs: SyncLog[],
  locationSettings: LocationInventorySync[],
  now = dayjs(),
): SyncHealthJob[] => {
  const disabledLocationNames = new Set(
    locationSettings
      .filter(location => location.excludeFromTraverseInventorySync && location.name)
      .map(location => location.name as string),
  );

  return syncLogs.flatMap((log): SyncHealthJob[] => {
    if (log.syncObject === 'Inventory' && disabledLocationNames.has(log.propertyName)) {
      return [];
    }

    const propertyAlias = propertyAliases[log.propertyName] || log.propertyName;
    if (propertyAlias === 'ShowroomsInventory') return [];

    const categoryKey = normalizedCategory(log.syncObject);
    const frequency = frequencyLabels[propertyAlias] || frequencyLabels[categoryKey] || 'Unknown';
    const lastCompletedAt = getLastCompletedAt(log);
    const minutes = frequencyMinutes[frequency] || null;
    const recordsProcessed = log.objectCount ?? log.shopifyBulkObjectCount ?? 0;

    return [{
      id: String(log.id),
      categoryKey,
      category: categoryDisplayNames[categoryKey] || categoryKey,
      name: propertyDisplayNames[log.propertyName] || log.propertyName,
      frequency,
      frequencyMinutes: minutes,
      recordsProcessed,
      health: calculateHealth(log, frequency, lastCompletedAt, now),
      lastCompletedAt,
      startedAt: log.startedAt || null,
      duration: formatDuration(log.startedAt || null, lastCompletedAt, now),
      nextRunAt: lastCompletedAt && minutes
        ? dayjs(lastCompletedAt).add(minutes, 'minute').toISOString()
        : null,
      errorMessage: getLatestError(log),
    }];
  });
};

export const groupSyncHealthJobs = (jobs: SyncHealthJob[]): SyncHealthGroup[] => {
  const groups = jobs.reduce((result, job) => {
    const group = result.get(job.categoryKey) || {
      key: job.categoryKey,
      name: job.category,
      jobs: [],
    };

    group.jobs.push(job);
    result.set(job.categoryKey, group);
    return result;
  }, new Map<string, SyncHealthGroup>());

  return Array.from(groups.values())
    .map(group => ({
      ...group,
      jobs: [...group.jobs].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      const aIndex = categoryOrder.indexOf(a.key);
      const bIndex = categoryOrder.indexOf(b.key);
      if (aIndex === -1 && bIndex === -1) return a.name.localeCompare(b.name);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
};

export const isIssue = (health: SyncHealth) => health === 'delayed' || health === 'failed';
